import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, Building2, Users } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api } from '../../api/client'
import { getFitMeta } from '../../utils/b2bFit'

const SCREENING_STYLES = {
  draft: 'bg-gray-100 text-gray-500',
  sent: 'bg-amber-100 text-amber-700',
  identity_verified: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-600',
  expired: 'bg-gray-100 text-gray-500',
}
const screeningLabel = (s) =>
  s === 'sent' ? 'Awaiting' :
  s === 'identity_verified' ? 'Verified' :
  s === 'in_progress' ? 'In progress' :
  s === 'completed' ? 'Completed' :
  s === 'declined' ? 'Declined' :
  s === 'expired' ? 'Expired' :
  s === 'draft' ? 'Draft' : 'No screening'

export default function CandidatesScreen() {
  const [companies, setCompanies] = useState([])
  const [jobs, setJobs] = useState([])
  const [roster, setRoster] = useState([])
  const [companyFilter, setCompanyFilter] = useState('')
  const [jobFilter, setJobFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Filter options — companies + jobs in the workspace.
  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get('/api/b2b/companies').catch(() => []),
      api.get('/api/b2b/jobs').catch(() => []),
    ]).then(([cs, js]) => {
      if (cancelled) return
      setCompanies(cs)
      setJobs(js)
    })
    return () => { cancelled = true }
  }, [])

  // Roster — refetched whenever a filter changes.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const params = new URLSearchParams()
    if (companyFilter) params.set('company_id', companyFilter)
    if (jobFilter) params.set('job_id', jobFilter)
    const qs = params.toString()
    api.get(`/api/b2b/candidates${qs ? `?${qs}` : ''}`)
      .then((data) => { if (!cancelled) setRoster(data) })
      .catch(() => { if (!cancelled) setError('Could not load candidates.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [companyFilter, jobFilter])

  // Job dropdown narrows to the selected company.
  const jobOptions = useMemo(
    () => (companyFilter ? jobs.filter((j) => j.company_id === companyFilter) : jobs),
    [jobs, companyFilter],
  )

  const handleCompanyChange = (value) => {
    setCompanyFilter(value)
    // If the chosen job no longer belongs to the company, clear it.
    if (jobFilter && value) {
      const stillValid = jobs.some((j) => j.id === jobFilter && j.company_id === value)
      if (!stillValid) setJobFilter('')
    }
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-ink-1">Candidates</h1>
          <p className="text-sm text-ink-3 mt-1">Every candidate across your jobs, ranked by AI fit.</p>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <FilterSelect
            icon={Building2}
            value={companyFilter}
            onChange={handleCompanyChange}
            placeholder="All companies"
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
          />
          <FilterSelect
            icon={Briefcase}
            value={jobFilter}
            onChange={setJobFilter}
            placeholder="All jobs"
            options={jobOptions.map((j) => ({ value: j.id, label: j.title }))}
          />
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
        )}

        <div className="mt-8">
          {loading ? (
            <p className="text-sm text-ink-3">Loading…</p>
          ) : roster.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-card p-12 text-center">
              <div className="w-12 h-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mx-auto">
                <Users className="w-5 h-5" />
              </div>
              <h2 className="mt-4 text-lg font-bold text-ink-1">No candidates</h2>
              <p className="mt-1 text-sm text-ink-3 max-w-sm mx-auto">
                {companyFilter || jobFilter ? 'No candidates match these filters.' : 'Add candidates from a job to see them here.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {roster.map((row) => <RosterRow key={`${row.job_id}-${row.candidate_id}`} row={row} />)}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}

function RosterRow({ row }) {
  const meta = getFitMeta(row.fit_verdict)
  return (
    <Link
      to={`/b2b/jobs/${row.job_id}/candidates/${row.candidate_id}`}
      className="bg-white rounded-2xl shadow-card hover:shadow-card-hover p-5 flex items-center gap-4 transition"
    >
      <div className="flex-1 min-w-0">
        <h2 className="text-base font-bold text-ink-1 truncate">{row.candidate_name}</h2>
        <p className="text-xs text-ink-3 mt-1 truncate">
          {[row.job_title, row.company_name].filter(Boolean).join(' • ') || 'No job'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {meta && (
          <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${meta.badgeClass}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`} />
            {meta.label}
          </span>
        )}
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${SCREENING_STYLES[row.screening_status] || 'bg-gray-100 text-gray-500'}`}>
          {screeningLabel(row.screening_status)}
        </span>
      </div>
    </Link>
  )
}

function FilterSelect({ icon, value, onChange, placeholder, options }) {
  const Icon = icon
  return (
    <div className="inline-flex items-center gap-2 rounded-2xl border border-black/10 bg-white px-3 py-2.5 focus-within:border-accent transition-colors">
      <Icon className="w-4 h-4 text-ink-3" />
      <select
        className="text-sm outline-none bg-transparent text-ink-1 pr-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}
